const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

class OpenAIService {
  constructor() {
    this.dealershipContext = `
You are AutoBot, the AI assistant for Shawn Ryder Digital Demo Motors Halifax, an innovative dealership platform in Halifax, Nova Scotia, Canada.

DEALERSHIP INFORMATION:
- Owner: Shawn Ryder (Website: shawnryder.com)
- Location: Halifax, Nova Scotia, Canada
- Address: 1234 Robie Street, Halifax, NS B3H 3E2
- Specializes in premium vehicles with AI-powered customer service
- Services: Sales, Service, Parts, Financing, Trade-ins
- Hours: Sales (Mon-Sat 9AM-8PM AST), Service (Mon-Fri 7AM-6PM AST, Sat 8AM-4PM AST)
- Website: Visit shawnryder.com for more digital solutions

HALIFAX & NOVA SCOTIA FOCUS:
- Serving Halifax Regional Municipality and Maritime provinces
- Understanding of Canadian automotive regulations and standards
- Knowledge of Nova Scotia climate considerations (winter driving, salt corrosion)
- Familiar with local financing options and Canadian credit systems
- Experience with Maritime weather conditions and vehicle requirements
- Understanding of HST (15%) and Canadian pricing
- Knowledge of Nova Scotia Motor Vehicle Act requirements

INVENTORY FOCUS:
- Premium German vehicles (Audi, BMW, Mercedes-Benz)
- All-wheel drive vehicles popular in Halifax winters
- Electric and hybrid vehicles suitable for Nova Scotia climate
- Certified pre-owned with comprehensive warranties
- Latest technology and safety features for Maritime driving conditions

LOCAL EXPERTISE:
- Winter tire recommendations for Halifax winters
- Vehicle preparation for Maritime salt air and road conditions
- Understanding of local inspection and registration processes
- Knowledge of Halifax traffic patterns and driving conditions
- Familiarity with Canadian automotive financing terms

CONVERSATION STYLE:
- Be helpful, professional, and conversational
- Use Canadian spelling and terminology (colour, centre, kilometres)
- All pricing in Canadian dollars (CAD)
- Keep responses concise but informative (2-3 paragraphs max)
- Reference shawnryder.com when discussing digital innovation
- Always offer to help with next steps or additional questions

Always be helpful, professional, and knowledgeable about automotive topics, dealership services, and Halifax/Nova Scotia specific considerations.
    `;
  }

  async sendMessage(message, conversationHistory = []) {
    try {
      // Check if API key is available
      if (!OPENAI_API_KEY || OPENAI_API_KEY === 'your-openai-api-key-here') {
        return this.getFallbackResponse(message);
      }

      const messages = [
        { role: 'system', content: this.dealershipContext },
        ...conversationHistory,
        { role: 'user', content: message }
      ];

      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: messages,
          max_tokens: 500,
          temperature: 0.7,
          presence_penalty: 0.1,
          frequency_penalty: 0.1
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('OpenAI Service Error:', error);
      return this.getFallbackResponse(message);
    }
  }

  getFallbackResponse(message) {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('awd') || lowerMessage.includes('winter') || lowerMessage.includes('suv')) {
      return `Great question about AWD vehicles for Halifax winters! 🚗❄️

For Maritime driving conditions, I'd recommend our Audi Q5 Quattro or BMW X3 xDrive. Both feature advanced all-wheel drive systems perfect for Nova Scotia winters, with prices starting around $48,999 CAD.

Key features for Halifax drivers:
• Quattro/xDrive AWD for winter traction
• Heated seats and steering wheel
• Advanced safety systems
• Premium sound systems for those Maritime road trips

Would you like to schedule a test drive or learn more about financing options? Our Halifax team is ready to help! Visit shawnryder.com for more information.`;
    }
    
    if (lowerMessage.includes('trade') || lowerMessage.includes('value')) {
      return `I'd be happy to help with your Halifax trade-in estimate! 💰

To provide an accurate CAD valuation, I'll need:
• Year, make, and model of your vehicle
• Current kilometres
• Overall condition
• Service history

Our AI considers Maritime-specific factors like:
• Salt corrosion from Halifax winters
• Local market demand in Nova Scotia
• Seasonal pricing trends
• Canadian depreciation patterns

The average trade-in value in Halifax has increased by 8% this year! Would you like to start the estimation process or visit our showroom at 1234 Robie Street for an in-person appraisal?`;
    }
    
    if (lowerMessage.includes('service') || lowerMessage.includes('appointment') || lowerMessage.includes('tire')) {
      return `Perfect timing for Halifax service! 🔧

Our service department specializes in:
• Winter tire installation/storage
• Maritime climate vehicle prep
• German luxury vehicle maintenance
• Nova Scotia MVI inspections

Available appointments:
• Monday-Friday: 7AM-6PM AST
• Saturday: 8AM-4PM AST
• Same-day service often available

We offer complimentary shuttle service within Halifax Regional Municipality, plus a comfortable waiting area with free WiFi and refreshments.

Would you like me to check availability for this week? What type of service do you need?`;
    }
    
    if (lowerMessage.includes('financing') || lowerMessage.includes('payment') || lowerMessage.includes('loan')) {
      return `Excellent! Let me help with Halifax financing options! 💳

Current Canadian rates starting at:
• 4.9% APR for qualified buyers
• Up to 84-month terms available
• $0 down payment options
• Same-day approval possible

Halifax-specific benefits:
• HST (15%) financing available
• Maritime credit union partnerships
• Flexible payment schedules
• Pre-approval in minutes

Example: A $45,000 vehicle could be as low as $687/month CAD with our current promotions.

Would you like to start a pre-approval application or discuss specific monthly payment targets? Our finance team can work with all credit situations!`;
    }
    
    // Default response
    return `Thanks for your question! I'm here to help with all your Halifax automotive needs. 🚗

I can assist with:
• Finding the perfect vehicle for Maritime driving
• Trade-in valuations in CAD
• Service scheduling and maintenance
• Canadian financing and lease options
• Winter driving recommendations

Our Halifax showroom features premium Audi, BMW, and Mercedes-Benz vehicles, all prepared for Nova Scotia's unique driving conditions.

What specific area would you like to explore? I'm powered by advanced AI from shawnryder.com and ready to help make your car buying experience seamless!`;
  }

  async getVehicleRecommendations(preferences) {
    const prompt = `
Based on these customer preferences for Halifax, Nova Scotia:
${JSON.stringify(preferences)}

Recommend 3 vehicles from our premium inventory considering:
- Halifax winter driving conditions and all-wheel drive needs
- Canadian safety standards and requirements
- Maritime climate considerations (salt air, humidity)
- Nova Scotia fuel efficiency preferences
- Latest technology and safety features suitable for Canadian roads
- HST (15%) included pricing considerations

Provide specific model recommendations with reasons why they're perfect matches for Halifax drivers.
    `;
    return await this.sendMessage(prompt);
  }

  async calculateTradeValue(vehicleInfo) {
    const prompt = `
Calculate a trade-in estimate for this vehicle in Halifax, Nova Scotia:
${JSON.stringify(vehicleInfo)}

Consider:
- Current Canadian automotive market conditions
- Halifax/Maritime regional market factors
- Vehicle condition in Maritime climate (salt corrosion, winter wear)
- Seasonal demand factors in Nova Scotia
- Canadian depreciation patterns and resale values
- Local market demand in Halifax Regional Municipality

Provide a realistic CAD range and factors affecting the value in the Halifax market.
    `;
    return await this.sendMessage(prompt);
  }

  async generateFinanceAdvice(financingQuery) {
    const prompt = `
Provide financing advice for this Halifax, Nova Scotia customer query:
Query: ${financingQuery}

Consider:
- Current Canadian interest rates and Bank of Canada policies
- Nova Scotia provincial financing incentives and programs
- Canadian credit scoring and lending practices
- HST (15%) calculations and financing options
- Maritime-specific financing considerations
- Canadian automotive financing terms and regulations

Provide clear, helpful financial guidance for Halifax customers.
    `;
    return await this.sendMessage(prompt);
  }

  async scheduleServiceAdvice(serviceRequest) {
    const prompt = `
Help schedule a service appointment for Halifax, Nova Scotia based on this request:
${serviceRequest}

Our Halifax service department details:
- Location: 1234 Robie Street, Halifax, NS B3H 3E2
- Hours: Mon-Fri 7AM-6PM AST, Sat 8AM-4PM AST
- Services: Oil changes, winter tire installation, inspections, warranty work, collision repair
- Specializes in German luxury vehicles and Maritime climate preparation
- Winter vehicle preparation and undercoating services
- Shuttle service available within Halifax Regional Municipality
- MVI (Motor Vehicle Inspection) services for Nova Scotia requirements

Provide scheduling options and Halifax-specific service recommendations.
    `;
    return await this.sendMessage(prompt);
  }

  async generateVehicleComparison(vehicles) {
    const prompt = `
Compare these vehicles for a Halifax, Nova Scotia customer:
${vehicles.join(' vs ')}

Provide a detailed comparison covering:
- Performance in Halifax winter conditions
- All-wheel drive capabilities for Maritime weather
- Features and technology suitable for Canadian roads
- Pricing and value in Canadian market (CAD, including HST)
- Reliability and maintenance in Maritime climate
- Technology and safety features for Nova Scotia driving
- Pros and cons of each for Halifax drivers

Make a recommendation based on different Halifax customer needs and driving conditions.
    `;
    return await this.sendMessage(prompt);
  }
}

export default new OpenAIService();